import { Controller } from "@hotwired/stimulus"
import { PASSWORD_RULES } from "../utils/passwordRules"

const REQUIREMENTS_ID = "password-requirements"
const ALERT_ID = "password-signup-alert"

export default class extends Controller {
  static targets = ["password", "confirmation", "alert", "passwordToggle", "confirmationToggle", "rule"]
  static values = {
    mismatchMessage: { type: String, default: "Passwords don't match." },
    rulesMessage: { type: String, default: "Password does not comply with our password rules." }
  }

  connect() {
    this.validate()
  }

  validate() {
    const password = this.hasPasswordTarget ? (this.passwordTarget.value || "") : ""
    const confirmation = this.hasConfirmationTarget ? (this.confirmationTarget.value || "") : ""

    this.updateRules(password)

    const rulesOk = Object.values(PASSWORD_RULES).every((rule) => rule(password))

    const confirmationPresent = confirmation.length > 0
    const mismatch = confirmationPresent && password !== confirmation

    if (mismatch) {
      this.showAlert(this.mismatchMessageValue)
      this.markInvalid(true, true)
      this.updateDescribedBy({ passwordAlert: true, confirmationAlert: true })
      return
    }

    if (password.length > 0 && !rulesOk) {
      this.showAlert(this.rulesMessageValue)
      this.markInvalid(true, false)
      this.updateDescribedBy({ passwordAlert: true, confirmationAlert: false })
      return
    }

    this.hideAlert()
    this.markInvalid(false, false)
    this.updateDescribedBy({ passwordAlert: false, confirmationAlert: false })
  }

  updateDescribedBy({ passwordAlert, confirmationAlert }) {
    const alertIdAvailable = this.hasAlertTarget && this.alertTarget.id === ALERT_ID

    if (this.hasPasswordTarget) {
      const ids = []
      if (this.hasRuleTarget) ids.push(REQUIREMENTS_ID)
      if (passwordAlert && alertIdAvailable) ids.push(ALERT_ID)
      if (ids.length > 0) {
        this.passwordTarget.setAttribute("aria-describedby", ids.join(" "))
      } else {
        this.passwordTarget.removeAttribute("aria-describedby")
      }
    }

    if (this.hasConfirmationTarget) {
      if (confirmationAlert && alertIdAvailable) {
        this.confirmationTarget.setAttribute("aria-describedby", ALERT_ID)
      } else {
        this.confirmationTarget.removeAttribute("aria-describedby")
      }
    }
  }

  updateRules(password) {
    if (!this.hasRuleTarget) return

    const hasPassword = password.length > 0

    this.ruleTargets.forEach((el) => {
      const rule = PASSWORD_RULES[el.dataset.rule]
      const satisfied = hasPassword && !!rule && rule(password)
      el.classList.toggle("is-satisfied", satisfied)

      const label = el.querySelector(".password-requirement__text")?.textContent?.trim()
      const status = satisfied ? "met" : "not met"
      el.setAttribute("aria-label", label ? `${label}: ${status}` : status)
    })
  }

  validateOnSubmit(event) {
    this.validate()
    if (this.isShowingAlert()) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  showAlert(message) {
    if (!this.hasAlertTarget) return
    this.alertTarget.textContent = message
    this.alertTarget.hidden = false
    this.alertTarget.classList.remove("d-none")
  }

  hideAlert() {
    if (!this.hasAlertTarget) return
    this.alertTarget.textContent = ""
    this.alertTarget.hidden = true
    this.alertTarget.classList.add("d-none")
  }

  isShowingAlert() {
    return this.hasAlertTarget && !this.alertTarget.hidden
  }

  markInvalid(passwordInvalid, confirmationInvalid) {
    if (this.hasPasswordTarget) {
      this.toggleInvalidClasses(this.passwordTarget, passwordInvalid)
    }
    if (this.hasConfirmationTarget) {
      this.toggleInvalidClasses(this.confirmationTarget, confirmationInvalid)
    }
  }

  toggleInvalidClasses(el, invalid) {
    el.classList.toggle("password-input-error", invalid)
    el.setAttribute("aria-invalid", invalid ? "true" : "false")
  }

  togglePasswordVisibility() {
    if (!this.hasPasswordTarget) return
    this.toggleVisibility(this.passwordTarget, this.hasPasswordToggleTarget ? this.passwordToggleTarget : null, "password")
  }

  toggleConfirmationVisibility() {
    if (!this.hasConfirmationTarget) return
    this.toggleVisibility(this.confirmationTarget, this.hasConfirmationToggleTarget ? this.confirmationToggleTarget : null, "password confirmation")
  }

  toggleVisibility(input, button, fieldLabel) {
    const showing = input.type === "text"
    input.type = showing ? "password" : "text"
    if (button) {
      button.textContent = showing ? "Show" : "Hide"
      button.setAttribute("aria-label", `${showing ? "Show" : "Hide"} ${fieldLabel}`)
    }
  }
}

